/**
 * What a workflow covers, in words, and what nothing covers. Pure.
 *
 * The rule itself lives on the server (functions/src/governance/approvalRouting.ts)
 * and the page asks it through `previewApprovalRouteFn`. These helpers only
 * describe saved coverage; they never decide a route.
 */

import type { ApprovalWorkflow } from './approvalsData';

interface Named { id: string; name: string }

export function coverIds(w: Pick<ApprovalWorkflow, 'covers'>): { types: string[]; departments: string[] } {
  return { types: w.covers?.documentTypeIds ?? [], departments: w.covers?.departmentIds ?? [] };
}

export function coversAnything(w: Pick<ApprovalWorkflow, 'covers'>): boolean {
  const c = coverIds(w);
  return c.types.length > 0 || c.departments.length > 0;
}

/** In play: it takes everything else, or it names something. Otherwise it is a spare nobody's document reaches. */
export function inUse(w: ApprovalWorkflow): boolean {
  return Boolean(w.isDefault) || coversAnything(w);
}

function listOf(ids: string[], all: readonly Named[]): string {
  const names = ids.map((id) => all.find((x) => x.id === id)?.name ?? 'a removed item');
  if (names.length <= 3) return names.join(', ');
  return `${names.slice(0, 3).join(', ')} and ${names.length - 3} more`;
}

/** "Clinical Protocol, Form from Pharmacy" — what the workflow is for, for the picker and the status line. */
export function coverageText(w: ApprovalWorkflow, types: readonly Named[], departments: readonly Named[]): string {
  if (w.isDefault) return 'everything no other workflow covers';
  const c = coverIds(w);
  if (c.types.length && c.departments.length) return `${listOf(c.types, types)} from ${listOf(c.departments, departments)}`;
  if (c.types.length) return listOf(c.types, types);
  if (c.departments.length) return `anything from ${listOf(c.departments, departments)}`;
  return 'nothing yet';
}

/**
 * Document types and departments no workflow names on its own, so they fall
 * to the workflow for everything else. A type named only together with a
 * department ("Forms from Pharmacy") still falls through everywhere else, so
 * it is listed.
 */
export function fallsToCatchAll(workflows: readonly ApprovalWorkflow[], types: readonly Named[], departments: readonly Named[]): { types: Named[]; departments: Named[] } {
  const typeOnly = new Set<string>();
  const departmentOnly = new Set<string>();
  for (const w of workflows) {
    if (w.isActive === false || w.isDefault) continue;
    const c = coverIds(w);
    if (c.types.length && !c.departments.length) c.types.forEach((t) => typeOnly.add(t));
    if (c.departments.length && !c.types.length) c.departments.forEach((d) => departmentOnly.add(d));
  }
  return {
    types: types.filter((t) => !typeOnly.has(t.id)),
    departments: departments.filter((d) => !departmentOnly.has(d.id)),
  };
}
